import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getToken, peekToken, getEmail, getRoles, clearToken, isTokenExpired, tokenTimeLeft, refreshSession, logoutSession } from './token';

// Shared auth state for the whole app (public header + admin/broker portals).
// Backed by the shared token store (localStorage + `authToken` cookie) and the
// refresh cookie the API keeps.
//
// It also owns the global auth modal: any part of the app can call openLogin() /
// openSignup(), and the modal is opened automatically when a session can't be
// refreshed or a request comes back 401.

const AuthContext = createContext(null);

// Refresh this far before the access token expires so requests never race the expiry.
const REFRESH_AHEAD_MS = 2 * 60 * 1000;

const loggedOut = { token: null, email: null, roles: [] };

const readAuth = () => {
    const token = getToken(); // null if missing OR expired (and cleared)
    if (!token) return loggedOut;
    return { token, email: getEmail(), roles: getRoles() };
};

export function AuthProvider({ children }) {
    const [state, setState] = useState(() => readAuth());
    const [authModalMode, setAuthModalMode] = useState(null); // 'login' | 'signup' | null

    const openLogin = useCallback(() => setAuthModalMode('login'), []);
    const openSignup = useCallback(() => setAuthModalMode('signup'), []);
    const closeAuthModal = useCallback(() => setAuthModalMode(null), []);

    const refresh = useCallback(() => setState(readAuth()), []);

    const handleLoginSuccess = useCallback(() => {
        setState(readAuth());
        setAuthModalMode(null);
    }, []);

    const logout = useCallback(() => {
        setState(loggedOut);
        logoutSession();
    }, []);

    // Keep the session alive: refresh shortly before the access token expires, and
    // silently restore a session on load (the refresh cookie outlives the token).
    // If a token existed but can't be refreshed, drop to logged-out and prompt.
    useEffect(() => {
        const syncFromToken = async ({ tryRestore }) => {
            const raw = peekToken();
            const valid = raw && !isTokenExpired(raw);

            if (valid) {
                if (tokenTimeLeft() < REFRESH_AHEAD_MS) await refreshSession();
                setState((prev) => (prev.token === peekToken() ? prev : readAuth()));
                return;
            }

            const hadToken = !!raw;
            if (raw) clearToken();
            if (hadToken || tryRestore) {
                const refreshed = await refreshSession();
                if (refreshed) {
                    setState(readAuth());
                    return;
                }
            }
            setState((prev) => (prev.token ? loggedOut : prev));
            // Only nag someone whose session actually lapsed — not an anonymous
            // visitor who never logged in.
            if (hadToken) setAuthModalMode((mode) => mode || 'login');
        };

        const onFocus = () => syncFromToken({ tryRestore: false });
        const onUnauthorized = () => {
            setState(loggedOut);
            setAuthModalMode((mode) => mode || 'login');
        };

        window.addEventListener('focus', onFocus);
        window.addEventListener('auth:unauthorized', onUnauthorized);
        const intervalId = setInterval(onFocus, 60 * 1000);
        syncFromToken({ tryRestore: true });

        return () => {
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('auth:unauthorized', onUnauthorized);
            clearInterval(intervalId);
        };
    }, []);

    const value = useMemo(
        () => ({
            isLoggedIn: !!state.token,
            email: state.email,
            roles: state.roles,
            isAdmin: state.roles.includes('Admin'),
            isBroker: state.roles.includes('Broker') || state.roles.includes('Admin'),
            authModalMode,
            openLogin,
            openSignup,
            closeAuthModal,
            handleLoginSuccess,
            logout,
            refresh,
        }),
        [state.token, state.email, state.roles, authModalMode, openLogin, openSignup, closeAuthModal, handleLoginSuccess, logout, refresh]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        // Defensive fallback so calls don't crash if used outside the provider.
        return {
            isLoggedIn: false,
            email: null,
            roles: [],
            isAdmin: false,
            isBroker: false,
            authModalMode: null,
            openLogin: () => {},
            openSignup: () => {},
            closeAuthModal: () => {},
            handleLoginSuccess: () => {},
            logout: () => {},
            refresh: () => {},
        };
    }
    return ctx;
}
